import React, { useState, useEffect, Suspense, lazy } from 'react';
import api from '../api';

const PDFExportButton       = lazy(() => import('../components/PDFExportButton'));
const EstimateDownloadButton = lazy(() => import('../components/EstimateDownloadButton'));

const getClickRate = (count) => {
  if (count >= 10000) return 1.00;
  if (count >= 5000)  return 1.50;
  if (count >= 500)   return 2.50;
  return 3.00;
};

const fmt = (n) => `$${(n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtSigned = (n) => (n >= 0 ? '+' : '') + fmt(n);
const fmtK = (n) => {
  if (n == null) return '—';
  return '$' + Math.round(n ?? 0).toLocaleString('en-US');
};

const Reports = () => {
  const [customers, setCustomers]         = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [loading, setLoading]             = useState(false);
  const [report, setReport]               = useState(null);
  const [status, setStatus]               = useState('');
  const [uploadFiles, setUploadFiles]     = useState([]);
  const [dataSource, setDataSource]       = useState('collect');
  const [estimateData, setEstimateData]   = useState(null);
  const [loadingEstimate, setLoadingEstimate] = useState(false);
  const [itAdminHourlyRate, setItAdminHourlyRate]       = useState(60);
  const [additionalAdminMinutes, setAdditionalAdminMinutes] = useState(0);
  const [creationMinutes, setCreationMinutes] = useState(20);
  const [savingRoiConfig, setSavingRoiConfig] = useState(false);

  useEffect(() => {
    api.get('/customers').then(({ data }) => setCustomers(data.customers || []));
  }, []);

  useEffect(() => {
    const selected = customers.find(c => c.customerId === selectedCustomer);
    setItAdminHourlyRate(selected?.itAdminHourlyRate ?? 60);
    setAdditionalAdminMinutes(selected?.additionalAdminMinutesPerWorkspace ?? 0);
    setCreationMinutes(selected?.creationMinutesPerWorkspace ?? 20);
  }, [selectedCustomer]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedCustomer) {
      setReport(null);
      setEstimateData(null);
      setStatus('');
      return;
    }
    const selected = customers.find(c => c.customerId === selectedCustomer);
    if (selected?.type === 'pricing_estimate') {
      setReport(null);
      setLoadingEstimate(true);
      api.get(`/estimates/${selectedCustomer}`)
        .then(({ data }) => setEstimateData(data))
        .catch(() => setEstimateData(null))
        .finally(() => setLoadingEstimate(false));
    } else {
      setEstimateData(null);
      api.get('/reports', { params: { customerId: selectedCustomer } })
        .then(({ data }) => setReport(data.report))
        .catch(() => setReport(null));
    }
  }, [selectedCustomer, customers]);

  const handleCollectData = async () => {
    if (!selectedCustomer) return;
    try {
      setLoading(true);
      setStatus('Connecting to customer AWS account and collecting WorkSpaces data...');
      await api.post('/collect', { customerId: selectedCustomer });
      setStatus('Collection complete. Generating report...');
      const { data } = await api.post('/reports/generate', { customerId: selectedCustomer });
      setReport(data.report);
      setStatus('Inspection complete. Ready for departure.');
    } catch (error) {
      console.error(error);
      setStatus(`Failed: ${error.response?.data?.error || error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadDataPackage = async () => {
    if (!selectedCustomer || !uploadFiles.length) return;
    try {
      setLoading(true);
      setStatus('Uploading data package...');
      const texts = await Promise.all(uploadFiles.map(f => f.text()));
      const packages = texts.map(t => JSON.parse(t));
      await api.post('/data/upload', { customerId: selectedCustomer, data: packages.length === 1 ? packages[0] : packages });
      setStatus('Upload complete. Generating report...');
      const { data } = await api.post('/reports/generate', { customerId: selectedCustomer });
      setReport(data.report);
      setStatus('Inspection complete. Ready for departure.');
    } catch (error) {
      console.error(error);
      setStatus(`Upload failed: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = async () => {
    if (!selectedCustomer) return;
    try {
      setLoading(true);
      setStatus('Regenerating report from existing data...');
      const { data } = await api.post('/reports/generate', { customerId: selectedCustomer });
      setReport(data.report);
      setStatus('Inspection complete. Ready for departure.');
    } catch (error) {
      setStatus(error.response?.data?.error || 'Failed to regenerate report.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRoiConfig = async () => {
    if (!selectedCustomer) return;
    try {
      setSavingRoiConfig(true);
      await api.put(`/customers/${selectedCustomer}`, {
        itAdminHourlyRate,
        additionalAdminMinutesPerWorkspace: additionalAdminMinutes,
        creationMinutesPerWorkspace: creationMinutes,
      });
      setCustomers(prev => prev.map(c => c.customerId === selectedCustomer
        ? { ...c, itAdminHourlyRate, additionalAdminMinutesPerWorkspace: additionalAdminMinutes, creationMinutesPerWorkspace: creationMinutes }
        : c));
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to save ROI assumptions');
    } finally {
      setSavingRoiConfig(false);
    }
  };

  const selectedCustomerObj = customers.find(c => c.customerId === selectedCustomer);
  const isEstimate = selectedCustomerObj?.type === 'pricing_estimate';

  return (
    <main className="container">
      <div className="card">
        <h2>Cost Optimization Report</h2>

        <div className="form-group">
          <label htmlFor="customerSelect">Customer *</label>
          <select
            id="customerSelect"
            value={selectedCustomer}
            onChange={(e) => setSelectedCustomer(e.target.value)}
          >
            <option value="">-- Choose a customer --</option>
            {customers.map((c) => (
              <option key={c.customerId} value={c.customerId}>
                {c.customerName}{c.type === 'pricing_estimate' ? ' (Estimate)' : ''}
              </option>
            ))}
          </select>
        </div>

        {/* ── Pre-Flight controls (hidden for pricing estimates) ── */}
        {selectedCustomer && !isEstimate && !report && !loading && !status && (
          <p style={{ color: 'var(--color-text-secondary)', marginBottom: '1rem' }}>No report yet for this customer.</p>
        )}

        {selectedCustomer && !isEstimate && (
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '0.875rem', flexWrap: 'wrap' }}>
              <label style={{ cursor: 'pointer', fontWeight: dataSource === 'collect' ? 'var(--font-weight-semibold)' : 'normal' }}>
                <input type="radio" value="collect" checked={dataSource === 'collect'} onChange={() => setDataSource('collect')} style={{ marginRight: 6, width: 'auto' }} />
                Collect via AWS role
              </label>
              <label style={{ cursor: 'pointer', fontWeight: dataSource === 'upload' ? 'var(--font-weight-semibold)' : 'normal' }}>
                <input type="radio" value="upload" checked={dataSource === 'upload'} onChange={() => setDataSource('upload')} style={{ marginRight: 6, width: 'auto' }} />
                Upload data package
              </label>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
              {dataSource === 'collect' && (
                <button onClick={handleCollectData} disabled={loading} className="btn-success">
                  {loading ? 'Working...' : report ? 'Re-run Pre-Flight' : 'Run Pre-Flight'}
                </button>
              )}

              {dataSource === 'upload' && (
                <>
                  <input type="file" accept=".json" multiple onChange={(e) => setUploadFiles([...e.target.files])} style={{ width: 'auto' }} />
                  {uploadFiles.length > 1 && (
                    <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>{uploadFiles.length} files selected</span>
                  )}
                  <button onClick={handleUploadDataPackage} disabled={loading || !uploadFiles.length}>
                    {loading ? 'Processing...' : 'Upload & Generate Report'}
                  </button>
                  <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', margin: '0.5rem 0 0', width: '100%' }}>
                    Need the collection script?{' '}
                    <a href="/scripts/Get-WorkSpacesData.ps1" download>Download Get-WorkSpacesData.ps1</a>
                  </p>
                </>
              )}

              {report && !loading && (
                <button onClick={handleRegenerate} className="btn-secondary">
                  Regenerate from existing data
                </button>
              )}
            </div>
          </div>
        )}

        {status && <div className="status-bar">{status}</div>}

        {/* ── Pre-Flight PDF export ── */}
        {report && (() => {
          const customerName = selectedCustomerObj?.customerName || selectedCustomer;
          const dateStr = new Date().toISOString().slice(0, 10);
          return (
            <div style={{ marginBottom: '1rem' }}>
              <Suspense fallback={<button disabled>Loading PDF…</button>}>
                <PDFExportButton
                  report={report}
                  customerName={customerName}
                  fileName={`clickimpact-${customerName}-${dateStr}.pdf`}
                  itAdminHourlyRate={itAdminHourlyRate}
                  additionalAdminMinutesPerWorkspace={additionalAdminMinutes}
                  creationMinutesPerWorkspace={creationMinutes}
                />
              </Suspense>
            </div>
          );
        })()}

        {/* ── Pre-Flight report sections (unchanged) ── */}
        {report && (
          <>
            <div className="card" style={{ marginTop: '0.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '1rem' }}>
                <h3 style={{ margin: 0 }}>Cost Summary</h3>
                {report.generatedAt && (
                  <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                    Generated {new Date(report.generatedAt).toLocaleString()}
                  </span>
                )}
              </div>

              {report.costNote && (
                <div style={{ background: 'rgba(227, 238, 20, 0.10)', border: '1px solid #E3EE14', borderLeft: '3px solid #C8D412', borderRadius: 'var(--radius-sm)', padding: '0.6rem 0.75rem', marginBottom: '1rem', fontSize: 13, color: '#3D3D00' }}>
                  Note: {report.costNote}
                </div>
              )}

              <table>
                <tbody>
                  <tr>
                    <td><strong>Total WorkSpaces</strong></td>
                    <td>{report.totalWorkspaces ?? 0}</td>
                  </tr>
                  <tr>
                    <td><strong>Estimated Monthly Cost</strong></td>
                    <td>{fmt(report.currentMonthlyCost)}</td>
                  </tr>
                  <tr>
                    <td><strong>Estimated Monthly Savings</strong></td>
                    <td>
                      {report.estimatedMonthlySavings != null
                        ? (() => {
                            const total = (report.estimatedMonthlySavings ?? 0) + (report.idleFindings?.savingsMonthlyUsd ?? 0);
                            return <span style={{ color: 'var(--color-green)', fontWeight: 'var(--font-weight-semibold)' }}>{fmt(total)}</span>;
                          })()
                        : <span style={{ color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>Requires usage data</span>}
                    </td>
                  </tr>
                  <tr>
                    <td><strong>Estimated Annual Savings</strong></td>
                    <td>
                      {report.estimatedMonthlySavings != null
                        ? (() => {
                            const total = (report.estimatedMonthlySavings ?? 0) + (report.idleFindings?.savingsMonthlyUsd ?? 0);
                            return <span style={{ color: 'var(--color-green)', fontWeight: 'var(--font-weight-semibold)' }}>{fmt(total * 12)}</span>;
                          })()
                        : <span style={{ color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>Requires usage data</span>}
                    </td>
                  </tr>
                </tbody>
              </table>

              {report.savingsNote && (
                <div style={{ background: 'rgba(29, 238, 161, 0.08)', border: '1px solid #1deea1', borderLeft: '3px solid #1deea1', borderRadius: 'var(--radius-sm)', padding: '0.6rem 0.75rem', marginTop: '1rem', fontSize: 13, color: '#064E3B' }}>
                  {report.savingsNote}
                </div>
              )}
            </div>

            <div className="card" style={{ marginTop: '1rem' }}>
              <h3 style={{ marginTop: 0, marginBottom: '0.25rem' }}>ROI Assumptions</h3>
              <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 0, marginBottom: '0.75rem' }}>
                Used in the ROI calculation below and the PDF. Saved with this customer for future visits.
              </p>
              <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label htmlFor="itAdminHourlyRate">IT admin hourly rate ($)</label>
                  <input
                    id="itAdminHourlyRate"
                    type="number"
                    min="0"
                    step="1"
                    value={itAdminHourlyRate}
                    onChange={(e) => setItAdminHourlyRate(Number(e.target.value))}
                    style={{ width: '120px' }}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label htmlFor="additionalAdminMinutes">Additional admin time savings per WorkSpace (min/mo)</label>
                  <input
                    id="additionalAdminMinutes"
                    type="number"
                    min="0"
                    step="5"
                    value={additionalAdminMinutes}
                    onChange={(e) => setAdditionalAdminMinutes(Number(e.target.value))}
                    style={{ width: '120px' }}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label htmlFor="creationMinutes">Admin time saved per WorkSpace created (min)</label>
                  <input
                    id="creationMinutes"
                    type="number"
                    min="0"
                    step="5"
                    value={creationMinutes}
                    onChange={(e) => setCreationMinutes(Number(e.target.value))}
                    style={{ width: '120px' }}
                  />
                </div>
                <button onClick={handleSaveRoiConfig} disabled={savingRoiConfig}>
                  {savingRoiConfig ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>

            {(() => {
              const wsCount          = report.totalWorkspaces ?? 0;
              const clickRate        = getClickRate(wsCount);
              const clickRateStr     = '$' + (clickRate % 1 === 0 ? clickRate.toFixed(0) : clickRate.toFixed(2));
              const clickMonthly     = wsCount * clickRate;
              const clickAnnual      = clickMonthly * 12;
              const itRateStr        = '$' + (itAdminHourlyRate % 1 === 0 ? itAdminHourlyRate.toFixed(0) : itAdminHourlyRate.toFixed(2));

              const costSavings       = report.estimatedMonthlySavings != null
                ? (report.estimatedMonthlySavings ?? 0) + (report.idleFindings?.savingsMonthlyUsd ?? 0)
                : null;
              const costSavingsAnnual = costSavings != null ? costSavings * 12 : null;

              // Onboarding time savings: configurable minutes per WorkSpace creation (last 90 days)
              const creates90d             = report.lifecycleEvents?.created ?? 0;
              const onboardingHrsPerMonth  = (creates90d / 3 * creationMinutes) / 60;
              const onboardingValueMonthly = onboardingHrsPerMonth * itAdminHourlyRate;
              const onboardingAnnual       = onboardingValueMonthly * 12;

              // Additional admin time savings from automation/unified dashboard (configurable above)
              const additionalValueMonthly = (additionalAdminMinutes * wsCount / 60) * itAdminHourlyRate;
              const additionalAnnual       = additionalValueMonthly * 12;

              const totalItValueMonthly = onboardingValueMonthly + additionalValueMonthly;
              const netMonthly       = (costSavings ?? 0) + totalItValueMonthly - clickMonthly;
              const netAnnual        = netMonthly * 12;
              const roi              = clickMonthly > 0 ? (netMonthly / clickMonthly) * 100 : null;
              const netColor         = netMonthly >= 0 ? 'var(--color-green)' : 'var(--color-red)';
              return (
                <div className="card" style={{ marginTop: '1rem' }}>
                  <h3 style={{ marginTop: 0, marginBottom: '0.25rem' }}>Your ROI with Click</h3>
                  <table>
                    <tbody>
                      <tr>
                        <td><strong>Click cost ({wsCount} × {clickRateStr}/mo)</strong></td>
                        <td><span style={{ color: 'var(--color-red)' }}>{fmt(clickMonthly)}/mo</span></td>
                        <td><span style={{ color: 'var(--color-red)', fontSize: 13 }}>{fmt(clickAnnual)}/yr</span></td>
                      </tr>
                      <tr>
                        <td><strong>Cost savings identified</strong></td>
                        <td>
                          {costSavings != null
                            ? <span style={{ color: 'var(--color-green)' }}>{fmt(costSavings)}/mo</span>
                            : <span style={{ color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>Requires usage data</span>}
                        </td>
                        <td>
                          {costSavingsAnnual != null && <span style={{ color: 'var(--color-green)', fontSize: 13 }}>{fmt(costSavingsAnnual)}/yr</span>}
                        </td>
                      </tr>
                      {onboardingValueMonthly > 0 && (
                        <tr>
                          <td><strong>Onboarding time savings ({onboardingHrsPerMonth.toFixed(1)} hrs/mo × {itRateStr}/hr)</strong></td>
                          <td><span style={{ color: 'var(--color-green)' }}>{fmt(onboardingValueMonthly)}/mo</span></td>
                          <td><span style={{ color: 'var(--color-green)', fontSize: 13 }}>{fmt(onboardingAnnual)}/yr</span></td>
                        </tr>
                      )}
                      {additionalValueMonthly > 0 && (
                        <tr>
                          <td><strong>Additional admin time savings ({wsCount} × {additionalAdminMinutes} min × {itRateStr}/hr)</strong></td>
                          <td><span style={{ color: 'var(--color-green)' }}>{fmt(additionalValueMonthly)}/mo</span></td>
                          <td><span style={{ color: 'var(--color-green)', fontSize: 13 }}>{fmt(additionalAnnual)}/yr</span></td>
                        </tr>
                      )}
                      <tr style={{ borderTop: '2px solid var(--color-border)' }}>
                        <td><strong>Net value after Click</strong></td>
                        <td>
                          <span style={{ color: netColor, fontWeight: 'var(--font-weight-semibold)' }}>{fmtSigned(netMonthly)}/mo</span>
                        </td>
                        <td>
                          <span style={{ color: netColor, fontWeight: 'var(--font-weight-semibold)', fontSize: 13 }}>{fmtSigned(netAnnual)}/yr</span>
                        </td>
                      </tr>
                      {roi != null && (
                        <tr>
                          <td><strong>ROI</strong></td>
                          <td colSpan={2}>
                            <span style={{ color: roi >= 0 ? 'var(--color-green)' : 'var(--color-red)', fontWeight: 'var(--font-weight-semibold)', fontSize: 15 }}>
                              {roi >= 0 ? '+' : ''}{roi.toFixed(0)}%
                            </span>
                            <span style={{ color: 'var(--color-text-secondary)', fontSize: 12, marginLeft: 8 }}>
                              {roi >= 0 ? 'value exceeds Click cost' : 'value does not yet cover Click cost'}
                            </span>
                          </td>
                        </tr>
                      )}
                      {costSavings == null && (
                        <tr>
                          <td colSpan={3} style={{ fontSize: 12, color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
                            Cost optimization savings require usage data and are not yet included above.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              );
            })()}

            {report.lifecycleEvents && (
              <div className="card" style={{ marginTop: '1rem' }}>
                <h3 style={{ marginTop: 0, marginBottom: '0.25rem' }}>
                  Operational Activity
                  <span style={{ fontWeight: 'normal', fontSize: 13, color: 'var(--color-text-secondary)', marginLeft: 8 }}>
                    last {Math.min(report.lifecycleEvents.periodDays ?? 90, 90)} days
                  </span>
                </h3>
                <p style={{ color: 'var(--color-text-secondary)', fontSize: 14, marginBottom: '1.25rem' }}>
                  Each of these changes required manual admin action. Click can automate these.
                </p>
                <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                  {[
                    { label: 'Created',    value: report.lifecycleEvents.created,    color: '#1801B4' },
                    { label: 'Terminated', value: report.lifecycleEvents.terminated, color: 'var(--color-red)' },
                    { label: 'Modified',   value: report.lifecycleEvents.modified,   color: '#5B49DE' },
                    { label: 'Rebuilt',    value: report.lifecycleEvents.rebuilt,    color: '#5B49DE' },
                  ].map(({ label, value, color }) => (
                    <div key={label} style={{ textAlign: 'center', minWidth: 80 }}>
                      <div style={{ fontSize: 34, fontWeight: 'var(--font-weight-semibold)', color, lineHeight: 1.1 }}>{value ?? 0}</div>
                      <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 4 }}>{label}</div>
                    </div>
                  ))}
                  <div style={{ textAlign: 'center', minWidth: 80 }}>
                    <div style={{ fontSize: 34, fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-primary)', lineHeight: 1.1 }}>
                      {(report.lifecycleEvents.created ?? 0) +
                       (report.lifecycleEvents.terminated ?? 0) +
                       (report.lifecycleEvents.modified ?? 0) +
                       (report.lifecycleEvents.rebuilt ?? 0)}
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 4 }}>Total changes</div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── Pricing Estimate sections ── */}
        {isEstimate && loadingEstimate && <div className="spinner" />}

        {isEstimate && !loadingEstimate && !estimateData && (
          <p style={{ color: 'var(--color-text-secondary)' }}>Could not load estimate data.</p>
        )}

        {isEstimate && estimateData && (() => {
          const totalBenefit = (estimateData.wsSavings ?? 0) + (estimateData.itSavings ?? 0);
          const dateStr = estimateData.createdAt
            ? new Date(estimateData.createdAt).toLocaleString()
            : '—';
          return (
            <>
              {/* Estimate header card */}
              <div className="card" style={{ marginTop: '0.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.75rem' }}>
                  <h3 style={{ margin: 0 }}>Pricing Estimate</h3>
                  <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>Saved {dateStr}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginBottom: '0.5rem' }}>
                  {[
                    { label: 'Total Users', value: estimateData.totalWsCount?.toLocaleString() },
                    { label: 'Est. Monthly Cost', value: fmtK(estimateData.totalMonthly) },
                    { label: 'Est. Annual Cost',  value: fmtK((estimateData.totalMonthly ?? 0) * 12), green: true },
                  ].map(({ label, value, green }) => (
                    <div key={label} style={{ background: 'var(--color-bg)', borderRadius: '6px', padding: '0.75rem 1rem', borderTop: `2px solid ${green ? 'var(--color-green)' : 'var(--color-primary)'}` }}>
                      <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.25rem' }}>{label}</div>
                      <div style={{ fontSize: '1.25rem', fontWeight: 600, color: green ? 'var(--color-green)' : 'var(--color-primary)' }}>{value}</div>
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: '0.5rem' }}>
                  Region: {estimateData.region} · OS: {estimateData.os === 'linux' ? 'Linux' : 'Windows'}
                </div>
              </div>

              {/* Groups table */}
              <div className="card" style={{ marginTop: '1rem' }}>
                <h3 style={{ marginTop: 0, marginBottom: '0.75rem' }}>User Groups</h3>
                <table>
                  <thead>
                    <tr>
                      <th>Group</th>
                      <th>Bundle</th>
                      <th>Running mode</th>
                      <th>Users</th>
                      <th>Per user/mo</th>
                      <th>Total/mo</th>
                      <th>Total/yr</th>
                    </tr>
                  </thead>
                  <tbody>
                    {estimateData.groups.map((g, idx) => {
                      const label = g.label || `Group ${idx + 1}`;
                      const mode = g.cheaperMode === 'always_on' ? 'Always-On' : `AutoStop (~${g.hours}h/mo)`;
                      return (
                        <tr key={idx}>
                          <td>{label}</td>
                          <td>
                            {g.bundleName || g.bundleKey}
                            <span style={{ display: 'block', fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
                              {(g.os || 'windows') === 'linux' ? 'Linux' : 'Windows'}
                            </span>
                          </td>
                          <td>
                            <span style={{ color: 'var(--color-green)', fontWeight: 500 }}>✓</span> {mode}
                            {g.rates && (
                              <span style={{ display: 'block', fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
                                {g.cheaperMode === 'auto_stop' && g.rates.auto_stop_hr && g.rates.auto_stop_base != null
                                  ? `$${g.rates.auto_stop_base.toFixed(2)}/mo + $${g.rates.auto_stop_hr.toFixed(2)}/hr`
                                  : `$${(g.rates.always_on ?? 0).toFixed(2)}/mo`}
                              </span>
                            )}
                          </td>
                          <td>{g.count}</td>
                          <td>{g.perUserMo != null ? fmt(g.perUserMo) : '—'}</td>
                          <td style={{ fontWeight: 500 }}>{fmtK(g.monthlyTotal)}</td>
                          <td>{fmtK((g.monthlyTotal ?? 0) * 12)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  {estimateData.groups.length > 1 && (
                    <tfoot>
                      <tr style={{ fontWeight: 600, background: 'var(--color-bg)' }}>
                        <td colSpan={3}><strong>Total</strong></td>
                        <td><strong>{estimateData.totalWsCount}</strong></td>
                        <td>—</td>
                        <td><strong>{fmtK(estimateData.totalMonthly)}</strong></td>
                        <td><strong>{fmtK((estimateData.totalMonthly ?? 0) * 12)}</strong></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>

              {/* ROI card */}
              <div className="card" style={{ marginTop: '1rem' }}>
                <h3 style={{ marginTop: 0, marginBottom: '0.25rem' }}>Click Pre-Flight ROI</h3>
                <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', fontStyle: 'italic', marginBottom: '0.75rem' }}>
                  Based on industry benchmarks (~20% cost reduction, 10 min/workspace/month admin time). Actual results will vary.
                </p>
                <table>
                  <tbody>
                    <tr>
                      <td><strong>WorkSpaces cost</strong></td>
                      <td><span>{fmtK(estimateData.totalMonthly)}/mo</span></td>
                      <td><span style={{ fontSize: 13 }}>{fmtK((estimateData.totalMonthly ?? 0) * 12)}/yr</span></td>
                    </tr>
                    <tr>
                      <td><strong>Est. cost savings (~20%)</strong></td>
                      <td><span style={{ color: 'var(--color-green)' }}>{fmtK(estimateData.wsSavings)}/mo</span></td>
                      <td><span style={{ color: 'var(--color-green)', fontSize: 13 }}>{fmtK((estimateData.wsSavings ?? 0) * 12)}/yr</span></td>
                    </tr>
                    <tr>
                      <td><strong>IT admin time savings (10 min/ws @ $60/hr)</strong></td>
                      <td><span style={{ color: 'var(--color-green)' }}>{fmtK(estimateData.itSavings)}/mo</span></td>
                      <td><span style={{ color: 'var(--color-green)', fontSize: 13 }}>{fmtK((estimateData.itSavings ?? 0) * 12)}/yr</span></td>
                    </tr>
                    <tr style={{ borderTop: '2px solid var(--color-border)', fontWeight: 600 }}>
                      <td><strong>Total benefit</strong></td>
                      <td><span style={{ color: 'var(--color-green)' }}>{fmtK(totalBenefit)}/mo</span></td>
                      <td><span style={{ color: 'var(--color-green)', fontSize: 13 }}>{fmtK(totalBenefit * 12)}/yr</span></td>
                    </tr>
                    <tr>
                      <td><strong>Click Pre-Flight cost</strong></td>
                      <td><span style={{ color: 'var(--color-red)' }}>{fmtK(estimateData.clickMonthly)}/mo</span></td>
                      <td><span style={{ color: 'var(--color-red)', fontSize: 13 }}>{fmtK((estimateData.clickMonthly ?? 0) * 12)}/yr</span></td>
                    </tr>
                    <tr>
                      <td><strong>ROI</strong></td>
                      <td colSpan={2}>
                        <span style={{ color: estimateData.roi >= 0 ? 'var(--color-green)' : 'var(--color-red)', fontWeight: 600, fontSize: 15 }}>
                          {estimateData.roi >= 0 ? '+' : ''}{Math.round(estimateData.roi)}%
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>

                <div style={{ marginTop: '1rem' }}>
                  <Suspense fallback={<button disabled>Preparing PDF…</button>}>
                    <EstimateDownloadButton
                      estimate={{
                        ...estimateData,
                        generatedAt: estimateData.createdAt,
                        groupResults: estimateData.groups,
                      }}
                      fileName={`WorkSpaces-Estimate-${(estimateData.prospectName || 'estimate').replace(/\s+/g, '-')}.pdf`}
                    />
                  </Suspense>
                </div>
              </div>
            </>
          );
        })()}
      </div>
    </main>
  );
};

export default Reports;
